import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, RotateCcw, BookOpen } from "lucide-react";
import { useToast } from "../hooks/useToast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAgentConfig, setAgentConfig } from "../services/whatsappApi";

const DEFAULT_PROMPT = `Tu es le conseiller de vente d'**Esprit Livre**, une librairie en ligne en Algérie...`;

const BotSettings = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, success, error } = useToast();
  const qc = useQueryClient();

  const { data: prompt = "", isLoading } = useQuery({
    queryKey: ["agent", "config", "system_prompt"],
    queryFn: ({ signal }) => getAgentConfig("system_prompt", signal),
  });

  const [editing, setEditing] = useState("");
  const [dirty, setDirty] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);

  const saveMutation = useMutation({
    mutationFn: (value) => setAgentConfig("system_prompt", value),
    onSuccess: () => {
      success("Prompt système mis à jour. Redémarrez l'agent pour appliquer.");
      qc.invalidateQueries({ queryKey: ["agent", "config"] });
      setDirty(false);
    },
    onError: (e) => error("Erreur : " + (e?.response?.data?.detail || e.message)),
  });

  useEffect(() => {
    if (prompt && !editing && !dirty) {
      setEditing(prompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt]);

  useEffect(() => {
    const text = editing || "";
    setWordCount(text.split(/\s+/).filter(Boolean).length);
    setCharCount(text.length);
  }, [editing]);

  const handleSave = () => {
    if (!editing) return;
    saveMutation.mutate(editing);
  };

  const handleReset = () => {
    setEditing(DEFAULT_PROMPT);
    setDirty(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <button
            onClick={() => navigate("/admin/whatsapp")}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-2 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à la messagerie
          </button>
          <h1 className="flex items-center gap-3 text-2xl font-bold text-gray-900">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm">
              <BookOpen className="h-5 w-5" />
            </span>
            Paramètres de l'agent IA
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Personnalisez le prompt système qui définit le comportement et la personnalité de l'agent.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50 hover:text-gray-900 transition-all active:scale-[0.97]"
          >
            <RotateCcw className="h-4 w-4" />
            Par défaut
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || saveMutation.isPending}
            className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all active:scale-[0.97] ${
              dirty
                ? "bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-emerald-200"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-4 mb-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="font-medium text-gray-700">{wordCount}</span> mots
        </span>
        <span className="w-px h-3 bg-gray-200" />
        <span className="flex items-center gap-1">
          <span className="font-medium text-gray-700">{charCount}</span> caractères
        </span>
      </div>

      {/* Editor */}
      <div className="relative">
        <textarea
          value={editing}
          onChange={(e) => {
            setEditing(e.target.value);
            setDirty(e.target.value !== prompt);
          }}
          className="w-full min-h-[500px] rounded-2xl border border-gray-200 bg-white p-6 text-sm leading-relaxed text-gray-800 shadow-sm resize-y focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all font-mono"
          placeholder="Collez ou écrivez le prompt système ici..."
          spellCheck={false}
        />

        {/* Footer hint */}
        <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
          <p>
            <span className="font-medium text-gray-500">💡 Conseil :</span> Le prompt système définit la personnalité, les règles et les connaissances de l'agent.
          </p>
          {dirty && (
            <span className="flex items-center gap-1 text-amber-600">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Modifications non enregistrées
            </span>
          )}
        </div>
      </div>

      {/* Toast notifications */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium shadow-lg backdrop-blur-sm ${
              t.type === "success"
                ? "bg-emerald-600 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            <span>{t.message}</span>
            <button onClick={() => removeToast(t.id)} className="ml-2 opacity-70 hover:opacity-100">✕</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BotSettings;
